<template>
   <div :class="col">
      <div class="mb-0 position-relative sa-field">
         <!-- Label above the box (main.js formStyle 'icons' and 'plain') -->
         <label v-if="label && !floating && !compact" :for="inputId" class="form-label">{{ label }}</label>

         <div :class="wrapClass" @focusin="focused = true" @focusout="focused = false">
            <!-- Icon in front (formStyle 'icons' only) -->
            <span v-if="showIcon" class="input-group-text"><i :class="icon"></i></span>

            <!-- File box: v-model gives the chosen File (or null) -->
            <input
               v-if="isFile"
               ref="input"
               v-bind="$attrs"
               :id="inputId"
               type="file"
               @change="onFileChange"
               class="form-control custom-tooltip-input"
            />

            <!-- Every other type: text, email, tel, url, number, date ... -->
            <input
               v-else
               ref="input"
               v-bind="$attrs"
               :id="inputId"
               :type="type"
               :value="modelValue"
               :placeholder="floating ? ' ' : placeholder"
               @input="$emit('update:modelValue', $event.target.value)"
               @click="openPicker"
               class="form-control custom-tooltip-input"
            />

            <!-- Floating label: inside the box, moves up onto the border (formStyle 'floating') -->
            <label v-if="floating" :for="inputId" class="sa-float-label">{{ label }}</label>
         </div>

         <!-- Grey note under the box, e.g. "The permission key cannot be changed" -->
         <small v-if="hint" class="text-muted d-block sa-core-color">{{ hint }}</small>

         <!-- Anything else under the box, e.g. a "Plan price: … use" link -->
         <slot />

         <!-- Above the box: on focus, and in red while the field is invalid -->
         <div v-if="tooltip" class="custom-tooltip">{{ tooltip }}<span class="tooltip-arrow"></span></div>
      </div>
   </div>
</template>

<script>
// One form box. Its look follows formStyle in main.js ($formStyle):
//   'icons'    label above + icon box in front
//   'plain'    label above, no icon box
//   'floating' label inside the box, moves up onto the border on focus or when filled
// compact: no label and no icon (table rows, e.g. the Dynamic Fields)
// type="file": v-model gives the File (or null). Setting it to null empties the box.
//
// Keep the class on the input fixed (no :class): $validateForm adds is-valid / is-invalid
// to it directly, and a Vue class binding would wipe them.
let nextId = 0;
const ALWAYS_UP = ["date", "time", "datetime-local", "month", "week", "file", "color"];

export default {
   name: "FormField",
   inheritAttrs: false, // required, readonly, min, step, @input, @change... go to the <input>
   props: {
      modelValue: { default: "" },
      label: { type: String, default: "" },
      type: { type: String, default: "text" },
      icon: { type: String, default: "" },
      placeholder: { type: String, default: "" },
      tooltip: { type: String, default: "" },
      hint: { type: String, default: "" },
      col: { type: String, default: "col-md-6" },
      id: { type: String, default: "" },
      compact: { type: Boolean, default: false },
   },
   emits: ["update:modelValue"],
   data() {
      return { autoId: "form-field-" + ++nextId, focused: false };
   },
   computed: {
      inputId() {
         return this.id || this.autoId;
      },
      formStyle() {
         return this.$formStyle || "icons";
      },
      isFile() {
         return this.type === "file";
      },
      floating() {
         return this.formStyle === "floating" && !!this.label && !this.compact;
      },
      showIcon() {
         return this.formStyle === "icons" && !!this.icon && !this.compact;
      },
      hasValue() {
         const v = this.modelValue;
         return v !== null && v !== undefined && v !== "";
      },
      wrapClass() {
         if (this.showIcon) return "input-group";
         if (this.floating) {
            return {
               "sa-float": true,
               "is-up": this.focused || this.hasValue || ALWAYS_UP.includes(this.type),
               "is-focused": this.focused,
            };
         }
         return "";
      },
   },
   watch: {
      // File box: follow v-model (null = empty the box; a File = show it, e.g. the Sample button)
      modelValue(value) {
         if (!this.isFile || !this.$refs.input) return;
         const input = this.$refs.input;
         if (!value) {
            if (input.value) input.value = "";
         } else if (value instanceof File && input.files[0] !== value) {
            try {
               const dt = new DataTransfer();
               dt.items.add(value);
               input.files = dt.files;
            } catch (e) {
               // older browsers: the file is still in v-model and is still sent
            }
         }
      },
   },
   methods: {
      onFileChange(event) {
         this.$emit("update:modelValue", event.target.files[0] || null);
      },
      // Date and time boxes: open the picker when the box is clicked
      openPicker(event) {
         if (!ALWAYS_UP.includes(this.type)) return;
         try {
            event.target.showPicker?.();
         } catch (e) {
            // some browsers only allow it on certain clicks
         }
      },
   },
};
</script>